/** Les sept modes, un bloc chacun, plus le tableau comparatif. */
import type { Page } from '../types.js';

export interface ModeRow {
  readonly name: string;
  readonly players: string;
  readonly duration: string;
}

/** Recapitulatif compare, en bas de page. */
export const MODE_TABLE: readonly ModeRow[] = [
  { name: 'Chasse commune', players: '3 à 10', duration: '15 à 20 min' },
  { name: 'Diffusion', players: '4 à 10', duration: '15 à 20 min' },
  { name: 'Chacun son tableau', players: '2 à 10', duration: '15 à 20 min' },
  { name: 'Un seul dessine', players: '3 à 6', duration: '20 à 35 min' },
  { name: 'Battle royale', players: '4 à 10', duration: '20 à 30 min' },
  { name: 'Concours', players: '3 à 10', duration: '10 à 15 min' },
];

export const MODES: Page = {
  title: 'Les modes de jeu de Retusch',
  summary:
    'Les six façons de jouer à Retusch : chasse commune, diffusion, chacun son tableau, un seul dessine, battle royale et concours. Déroulé, effectif conseillé et durée pour chacune.',
  sections: [
    {
      heading: 'Chasse commune',
      blocks: [
        'Le mode par défaut, et celui par lequel commencer.',
        'Tout le monde dessine sur le même tableau, chacun dans sa propre case. Quand les retouches sont rendues, l’œuvre revient entière et la chasse s’ouvre pour tous en même temps. Une retouche ne se trouve qu’une fois, par le premier à la repérer : elle s’affiche alors pour toute la table et sort du jeu. On ne trouve jamais la sienne, et une cachette que personne ne voit rapporte le maximum à son auteur.',
        'Idéal pour un grand groupe : il n’y a aucun temps mort, tout le monde cherche en même temps et chaque découverte se voit à l’écran de tous.',
        'Effectif conseillé : 3 à 10 joueurs.',
      ],
    },
    {
      heading: 'Diffusion',
      blocks: [
        'L’image miroir du mode « un seul dessine », pensée pour le direct.',
        'Tous les joueurs dessinent sur le même tableau, sauf l’hôte. Puis l’hôte est le seul à chercher, pendant que son public le regarde s’échiner. Il ne marque aucun point et ne figure pas au classement : la partie se joue entre ceux qui se cachent, lui anime la chasse. Il peut se tromper autant de fois qu’il veut, la règle de l’erreur unique ne s’applique pas à lui.',
        'Idéal pour une soirée en direct, et pour une raison précise : comme l’hôte ne dessine pas, les spectateurs ne voient jamais sa cachette se construire. Dans les autres modes, ils regardent le streamer peindre sa case, et la traque n’a plus de secret pour eux.',
        'Effectif conseillé : 4 à 10 joueurs. Gratuit pour toujours.',
      ],
    },
    {
      heading: 'Chacun son tableau',
      blocks: [
        'Chaque joueur reçoit sa propre œuvre et y cache sa retouche. À la traque, on vous confie le tableau d’un seul autre joueur, et vous avez un unique clic pour désigner la case repeinte. Pas de course, pas de concurrence : juste vous, un tableau, et une décision.',
        'Idéal en duo, ou quand on veut une partie calme où chacun prend le temps de regarder.',
        'Effectif conseillé : 2 à 10 joueurs.',
      ],
    },
    {
      heading: 'Un seul dessine',
      blocks: [
        'Une manche, un dessinateur, tous les autres à sa poursuite.',
        'À chaque manche, une seule personne reçoit un tableau. Les autres attendent, puis cherchent sa retouche, chacun noté sur son propre temps. Le dessinateur, lui, est noté sur le chercheur le plus rapide, et il revoit sa cachette figée pendant qu’on la traque. Le rôle tourne : le réglage de manches y compte des tours complets, parce qu’une partie n’est équitable que si chacun a dessiné autant de fois.',
        'Idéal pour un petit groupe qui veut se juger sur un pied d’égalité, un dessin après l’autre.',
        'Effectif conseillé : 3 à 6 joueurs, au-delà l’attente devient longue.',
      ],
    },
    {
      heading: 'Battle royale',
      blocks: [
        'La chasse commune, avec une sortie par manche.',
        'Même tableau partagé, même traque simultanée. Mais ici on ne compte aucun point : la seule question est de rester. Le joueur dont la retouche est découverte en premier quitte la partie. Les autres continuent de chercher jusqu’au bout de la manche, ce qui n’élimine plus personne mais donne à chacun son rang, affiché sur sa case. Si personne n’est trouvé, personne ne sort.',
        'Idéal pour finir une soirée : la tension monte à mesure que la table se vide, et la dernière manche se joue à deux.',
        'Effectif conseillé : 4 à 10 joueurs.',
      ],
    },
    {
      heading: 'Concours',
      blocks: [
        'Le seul mode où personne ne cherche.',
        'Tout le monde repeint la même case du même tableau. Puis chacun parcourt les retouches des autres, à son rythme, et vote pour celle qu’il trouve la plus réussie. Une voix reçue vaut un point à son auteur. Pendant le vote les auteurs sont anonymes, et on ne peut pas voter pour soi : les noms et les voix ne sortent qu’à la révélation.',
        'Idéal quand le groupe est mélangé, ou quand certains veulent dessiner sans la pression du chronomètre de la traque.',
        'Effectif conseillé : 3 à 10 joueurs.',
      ],
    },
  ],
};

export const MODES_PREMIUM = {
  heading: 'Modes gratuits et modes payants',
  blocks: [
    'La chasse commune et le mode diffusion sont gratuits, définitivement.',
    'Les quatre autres deviendront payants à la création d’une partie, et seulement à la création. Les rejoindre restera toujours gratuit : il suffit qu’une personne à table ait débloqué le jeu pour que tout le monde puisse y jouer, sans compte ni paiement.',
    'Le déblocage se fera par un achat unique, sans abonnement, et retirera aussi la publicité pour le compte qui l’a effectué. En attendant, tous les modes sont ouverts à tout le monde.',
  ],
} as const;
